// @ai-generated 2026-08-19T06:57:20+00:00 by malte.ulfers@tngtech.com using Claude Opus 5 (1M context)
import { type Message } from '@ag-ui/client';
import { Tooltip } from '@synqo/frontend-component-library';
import { IconChevronRight } from '@tabler/icons-react';
import { useState } from 'react';
import classes from './ToolCallSidebar.module.css';
import toneClasses from './toolCallTone.module.css';
import { hasToolError, type ToolCallTone } from './toolCallStatus';

interface ToolCallEntry {
    id: string;
    name: string;
    args: string;
    result: string | undefined;
}

interface ToolCallSidebarProps {
    messages: Message[];
    collapsed: boolean;
    onToggleCollapsed: () => void;
}

/**
 * Read-only side panel that lists every tool call made in the current conversation, in call
 * order. Reads the AG-UI message log, so entries persist for the lifetime of the thread regardless
 * of what the in-chat renderer decides to show.
 *
 * Each entry pairs an `AssistantMessage.toolCalls[]` item with its matching `ToolMessage`
 * (correlated by `toolCallId`) — the tool message's presence marks the call as complete, its
 * absence as still running.
 */
export function ToolCallSidebar({ messages, collapsed, onToggleCollapsed }: ToolCallSidebarProps) {
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const entries = extractToolCalls(messages);

    const toggleLabel = collapsed ? 'Tool-Aufrufe einblenden' : 'Tool-Aufrufe ausblenden';

    return (
        <div className={`${classes.sidebar} ${collapsed ? classes.collapsed : ''}`}>
            <div className={classes.header}>
                {!collapsed && <span className={classes.headerLabel}>Tool-Aufrufe</span>}
                {(!collapsed || entries.length > 0) && <span className={classes.headerCount}>{entries.length}</span>}
                <Tooltip label={toggleLabel}>
                    <button
                        type="button"
                        aria-label={toggleLabel}
                        className={`${classes.iconButton} ${classes.headerToggle}`}
                        onClick={onToggleCollapsed}
                    >
                        <IconChevronRight
                            size={16}
                            className={`${classes.chevron} ${collapsed ? classes.pointBack : ''}`}
                        />
                    </button>
                </Tooltip>
            </div>
            {collapsed ? null : entries.length === 0 ? (
                <div className={classes.empty}>Noch keine Tool-Aufrufe in diesem Chat.</div>
            ) : (
                <div className={classes.list}>
                    {entries.map((entry, index) => {
                        const isComplete = entry.result !== undefined;
                        // A completed call is not necessarily a successful one: a rejected command
                        // still returns, and only the result's isError flag tells them apart.
                        const isFailed = isComplete && hasToolError(entry.result);
                        const tone: ToolCallTone = !isComplete ? 'running' : isFailed ? 'failed' : 'succeeded';
                        const isExpanded = expandedId === entry.id;
                        return (
                            <div key={entry.id} className={`${classes.entry} ${toneClasses[tone]}`}>
                                <div
                                    className={classes.entryHeader}
                                    onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                                >
                                    <span className={classes.entryIndex}>{index + 1}</span>
                                    <span className={classes.entryDot} />
                                    <span className={classes.entryName}>{entry.name}</span>
                                    {isFailed && <span className={classes.entryFailedTag}>failed</span>}
                                    <IconChevronRight
                                        size={14}
                                        className={`${classes.entryChevron} ${isExpanded ? classes.pointDown : ''}`}
                                    />
                                </div>
                                {isExpanded && (
                                    <div className={classes.entryBody}>
                                        <div className={classes.entrySectionLabel}>Arguments</div>
                                        <pre className={classes.entryCode}>{prettyJson(entry.args)}</pre>
                                        {entry.result !== undefined && (
                                            <>
                                                <div className={classes.entrySectionLabel}>Result</div>
                                                <pre className={classes.entryCode}>{prettyJson(entry.result)}</pre>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function extractToolCalls(messages: readonly unknown[]): ToolCallEntry[] {
    const resultsByToolCallId = new Map<string, string>();
    for (const message of messages) {
        if (isRecord(message) && message.role === 'tool' && typeof message.toolCallId === 'string') {
            const content = typeof message.content === 'string' ? message.content : '';
            resultsByToolCallId.set(message.toolCallId, content);
        }
    }

    const entries: ToolCallEntry[] = [];
    for (const message of messages) {
        if (!isRecord(message) || message.role !== 'assistant') {
            continue;
        }
        const toolCalls = message.toolCalls;
        if (!Array.isArray(toolCalls)) {
            continue;
        }
        for (const call of toolCalls) {
            if (!isRecord(call) || typeof call.id !== 'string') {
                continue;
            }
            const fn = isRecord(call.function) ? call.function : {};
            const name = typeof fn.name === 'string' ? fn.name : 'unknown';
            const args = typeof fn.arguments === 'string' ? fn.arguments : '';
            entries.push({
                id: call.id,
                name,
                args,
                result: resultsByToolCallId.get(call.id),
            });
        }
    }
    return entries;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function prettyJson(raw: string): string {
    if (raw === '') {
        return '';
    }
    try {
        return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
        return raw;
    }
}
